import React, { useCallback, useState, useEffect, useRef } from 'react';
import {
  Sparkles,
  PenLine,
  TextSearch,
  Smile,
  Briefcase,
  FoldVertical,
  Type,
  List,
  Paperclip,
  Mic,
  Loader2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAi } from '../providers/AiProvider';
import { AI_CONFIG } from '../config';

const { TEMPLATES } = AI_CONFIG;

const AiMenu = ({ editor }) => {
  const [selectionActive, setSelectionActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [promptStage, setPromptStage] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [customFile, setCustomFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const fileInputRef = useRef(null);
  const mediaRecorder = useRef(null);
  const { handleAiRequest, handleVoiceRequest } = useAi();

  // Listen for selection changes
  useEffect(() => {
    if (!editor) return;

    const updateSelectionStatus = () => {
      setSelectionActive(!editor.state.selection.empty);
    };

    updateSelectionStatus();
    editor.on('selectionUpdate', updateSelectionStatus);
    editor.on('focus', updateSelectionStatus);
    editor.on('blur', updateSelectionStatus);

    return () => {
      editor.off('selectionUpdate', updateSelectionStatus);
      editor.off('focus', updateSelectionStatus);
      editor.off('blur', updateSelectionStatus);
    };
  }, [editor]);

  const handleAiAction = useCallback(
    async (template, customPrompt = '') => {
      if (!editor || !handleAiRequest) return;

      setOpen(false);

      const { view, state } = editor;
      const { from, to } = view.state.selection;
      const selectedText = state.doc.textBetween(from, to, '\n');

      // Don't run actions requiring selection if there isn't one (except AI_WRITER)
      if (template !== TEMPLATES.AI_WRITER && from === to) {
        console.warn('AI action requires text selection.');
        return;
      }

      const textToProcess = template === TEMPLATES.AI_WRITER ? '' : selectedText;
      const originalContentForNode = template === TEMPLATES.AI_WRITER ? customPrompt : selectedText;

      try {
        // Insert the placeholder AI node
        editor
          .chain()
          .focus()
          .createAiNode({
            originalContent: originalContentForNode,
            content: '',
            status: 'starting'
          })
          .run();

        const response = await handleAiRequest({
          template,
          selectedText: textToProcess,
          customPrompt,
          customFileData: customFile
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch AI response (status: ${response.status})`);
        }

        const reader = response.body.getReader();
        let accumulatedText = '';
        let currentStatus = 'running';

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            currentStatus = 'completed';
            break;
          }

          const decodedChunk = new TextDecoder().decode(value, { stream: true });
          const lines = decodedChunk.split('\n');

          for (const line of lines) {
            if (line.trim() === '' || line === 'data: [DONE]') continue;

            try {
              const parsedLine = JSON.parse(line.replace(/^data: /, ''));
              if (parsedLine.choices && parsedLine.choices[0]?.delta?.content) {
                accumulatedText += parsedLine.choices[0].delta.content;
                editor.commands.updateAiContent({
                  originalContent: originalContentForNode,
                  content: accumulatedText,
                  status: 'running'
                });
              }
            } catch (e) {
              console.error('Failed to parse stream chunk:', line, e);
            }
          }
        }

        editor.commands.updateAiContent({
          originalContent: originalContentForNode,
          content: accumulatedText,
          status: currentStatus
        });
      } catch (error) {
        console.error('Error during AI request:', error);
        editor.commands.updateAiContent({
          originalContent: originalContentForNode,
          content: `Error: ${error.message}`,
          status: 'rejected'
        });
      } finally {
        setCustomFile(null);
      }
    },
    [editor, handleAiRequest, customFile]
  );

  const handleVoice = useCallback(async () => {
    if (isProcessing) return;

    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorder.current = new MediaRecorder(stream);
        const audioChunks = [];

        mediaRecorder.current.ondataavailable = event => {
          audioChunks.push(event.data);
        };

        mediaRecorder.current.onstop = async () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
          setIsRecording(false);
          stream.getTracks().forEach(track => track.stop());

          try {
            setIsProcessing(true);
            const { text } = await handleVoiceRequest(audioBlob);
            setCustomPrompt(prev => prev + (prev ? ' ' : '') + text);
          } catch (error) {
            console.error('Error processing voice request:', error);
          } finally {
            setIsProcessing(false);
          }
        };

        mediaRecorder.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        setIsRecording(false);
        setIsProcessing(false);
      }
    } else {
      if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
        mediaRecorder.current.stop();
      }
    }
  }, [isRecording, isProcessing, handleVoiceRequest]);

  if (!editor) return null;

  const handleSelect = template => e => {
    e.preventDefault();
    if (template === TEMPLATES.SUGGEST_EDITS || template === TEMPLATES.AI_WRITER) {
      setActiveTemplate(template);
      setPromptStage(true);
    } else {
      handleAiAction(template);
    }
  };

  const handleGenerate = () => {
    handleAiAction(activeTemplate, customPrompt);
    setCustomPrompt('');
    setCustomFile(null);
    setPromptStage(false);
  };

  const handleBack = () => {
    setPromptStage(false);
    setCustomFile(null);
    setFileError('');
    setActiveTemplate(null);
  };

  const handleFileChange = e => {
    setFileError('');
    const file = e.target.files[0];

    if (!file) return;

    if (!file.type.includes('pdf')) {
      setFileError('Only PDF files are allowed');
      return;
    }

    const maxSize = AI_CONFIG.FILE_LIMITS.MAX_PDF_SIZE_MB * 1024 * 1024;
    if (file.size > maxSize) {
      setFileError(`File must be less than ${AI_CONFIG.FILE_LIMITS.MAX_PDF_SIZE_MB}MB`);
      return;
    }

    const reader = new FileReader();
    reader.onload = event => {
      const base64String = event.target.result.split(',')[1];
      setCustomFile({
        name: file.name,
        data: base64String
      });
    };
    reader.readAsDataURL(file);
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const removeFile = () => {
    setCustomFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button className="p-2 rounded hover:bg-gray-200" title="AI Assistant">
          <Sparkles size={18} className="text-blue-600" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        {promptStage ? (
          <div className="flex flex-col p-2">
            <div className="flex justify-between items-center mb-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
              >
                ← Back
              </Button>
              <button
                onClick={handleVoice}
                className={`p-2 rounded hover:bg-gray-200 ${isRecording ? 'bg-gray-200' : ''}`}
                title="Voice Input"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : isRecording ? (
                  <div className="w-4 h-4 rounded-full bg-red-500 animate-pulse" />
                ) : (
                  <Mic size={18} />
                )}
              </button>
            </div>
            <Textarea
              value={customPrompt}
              onChange={e => setCustomPrompt(e.target.value)}
              placeholder="Enter your prompt..."
              className="mb-2"
              rows={3}
            />
            {/* File attachment section */}
            <div className="flex flex-col mb-2">
              <input
                type="file"
                accept=".pdf"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />

              {customFile ? (
                <div className="flex items-center justify-between p-2 border rounded mb-1">
                  <span className="text-sm truncate max-w-[180px]">{customFile.name}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={removeFile}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleFileClick}
                  className="flex items-center gap-2"
                >
                  <Paperclip size={14} /> Attach PDF (optional)
                </Button>
              )}

              {fileError && (
                <span className="text-xs text-red-500 mb-1">{fileError}</span>
              )}
            </div>
            <Button onClick={handleGenerate} className="w-full">
              <Sparkles size={16} className="mr-2" /> Generate
            </Button>
          </div>
        ) : (
          <>
            {selectionActive ? (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="flex items-center gap-2">
                  <Sparkles size={16} /> AI Actions
                </DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.SUGGEST_EDITS)}>
                  <Sparkles size={16} className="mr-2" /> Suggest Edits
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.PROOF_READ)}>
                  <TextSearch size={16} className="mr-2" /> Proof Read
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Tone</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.FRIENDLY)}>
                  <Smile size={16} className="mr-2" /> Friendly
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.PROFESSIONAL)}>
                  <Briefcase size={16} className="mr-2" /> Professional
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.CONCISE)}>
                  <FoldVertical size={16} className="mr-2" /> Concise
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Summarize</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.SUMMARY)}>
                  <Type size={16} className="mr-2" /> Summary
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.KEY_POINTS)}>
                  <List size={16} className="mr-2" /> Key Points
                </DropdownMenuItem>
              </DropdownMenuGroup>
            ) : (
              <DropdownMenuGroup>
                <DropdownMenuLabel className="flex items-center gap-2">
                  <Sparkles size={16} /> AI Assistant
                </DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleSelect(TEMPLATES.AI_WRITER)}>
                  <PenLine size={16} className="mr-2" /> Prompt
                </DropdownMenuItem>
              </DropdownMenuGroup>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AiMenu;
